'use client';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Icon } from '../Icon';

export interface PaginationProps {
  /** 1-based current page. */
  page: number;
  /** Total number of pages. Provide this OR (`total` + `pageSize`). */
  pageCount?: number | undefined;
  /** Total item count — combined with `pageSize` to derive `pageCount` when omitted. */
  total?: number | undefined;
  pageSize?: number | undefined;
  onPageChange: (page: number) => void;
  /** Number of page buttons shown on each side of the current page. Default `1`. */
  siblingCount?: number | undefined;
  /** Accessible label for the nav landmark. Default `'Pagination'`. */
  'aria-label'?: string | undefined;
  className?: string | undefined;
}

type PageItem = number | 'ellipsis';

function range(start: number, end: number): number[] {
  if (end < start) return [];
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

/**
 * Builds the numbered-page-plus-ellipsis sequence (1, 2, …, 9, 10 style). Always
 * anchors the first and last page; fills in `siblingCount` neighbors around the
 * current page; collapses any remaining gap into a single 'ellipsis' marker.
 */
function buildPageItems(page: number, pageCount: number, siblingCount: number): PageItem[] {
  const totalNumbers = siblingCount * 2 + 5; // first + last + current + 2 siblings + 2 ellipses
  if (pageCount <= totalNumbers) return range(1, pageCount);

  const leftSibling = Math.max(page - siblingCount, 1);
  const rightSibling = Math.min(page + siblingCount, pageCount);
  const showLeftEllipsis = leftSibling > 2;
  const showRightEllipsis = rightSibling < pageCount - 1;

  const items: PageItem[] = [1];

  if (showLeftEllipsis) {
    items.push('ellipsis');
  } else {
    for (let p = 2; p < leftSibling; p++) items.push(p);
  }

  for (let p = leftSibling; p <= rightSibling; p++) {
    if (p !== 1 && p !== pageCount) items.push(p);
  }

  if (showRightEllipsis) {
    items.push('ellipsis');
  } else {
    for (let p = rightSibling + 1; p < pageCount; p++) items.push(p);
  }

  items.push(pageCount);
  return items;
}

function resolvePageCount(
  pageCount: number | undefined,
  total: number | undefined,
  pageSize: number | undefined,
): number {
  if (pageCount != null) return Math.max(1, pageCount);
  if (total != null && pageSize != null && pageSize > 0) {
    return Math.max(1, Math.ceil(total / pageSize));
  }
  return 1;
}

const NAV_BTN =
  'inline-flex h-8 w-8 items-center justify-center rounded-control text-secondary hover:bg-surface-sunken hover:text-primary disabled:opacity-disabled disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus';

// raw-button-ok-file: every <button> in this file is this pagination
// primitive's own prev/page-number/next control, not a page action — no
// <Button> conversion applies. (2026-07-09)

/**
 * Numbered pagination (09-table-data-grid-system.md §States): required on
 * finance/report/audit tables — auditors need stable page boundaries, so
 * infinite scroll is banned there; cursor "Load more" remains sanctioned
 * elsewhere per `conv-frontend`. Selected page = solid blue (`bg-selected` /
 * `text-selected-fg`) — selection is blue everywhere, never green (ADR-0114).
 */
export function Pagination({
  page,
  pageCount: pageCountProp,
  total,
  pageSize,
  onPageChange,
  siblingCount = 1,
  className,
  'aria-label': ariaLabel = 'Pagination',
}: PaginationProps) {
  const pageCount = resolvePageCount(pageCountProp, total, pageSize);
  const items = buildPageItems(page, pageCount, siblingCount);
  const atFirst = page <= 1;
  const atLast = page >= pageCount;

  function go(target: number) {
    const clamped = Math.min(Math.max(target, 1), pageCount);
    if (clamped !== page) onPageChange(clamped);
  }

  return (
    <nav
      aria-label={ariaLabel}
      className={['flex items-center gap-1', className].filter(Boolean).join(' ')}
    >
      <button
        type="button"
        aria-label="Previous page"
        disabled={atFirst}
        onClick={() => go(page - 1)}
        className={NAV_BTN}
      >
        <Icon as={ChevronLeft} size="sm" />
      </button>
      {items.map((item, i) =>
        item === 'ellipsis' ? (
          <span key={`ellipsis-${i}`} aria-hidden="true" className="select-none px-1.5 text-muted">
            …
          </span>
        ) : (
          <button
            key={item}
            type="button"
            aria-current={item === page ? 'page' : undefined}
            onClick={() => go(item)}
            className={[
              'inline-flex h-8 min-w-[2rem] items-center justify-center rounded-control px-2 text-body-sm-emphasis tabular-nums',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus',
              item === page
                ? 'bg-selected text-selected-fg' // selected = SOLID blue (ADR-0114)
                : 'text-secondary hover:bg-surface-sunken hover:text-primary',
            ].join(' ')}
          >
            {item}
          </button>
        ),
      )}
      <button
        type="button"
        aria-label="Next page"
        disabled={atLast}
        onClick={() => go(page + 1)}
        className={NAV_BTN}
      >
        <Icon as={ChevronRight} size="sm" />
      </button>
    </nav>
  );
}
